import { useContext } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { usePTS } from "../../context/PTSContext";
import { useRakennusData } from "../../context/RakennusDataContext";
import { ReportTemplateContext } from "../report/ReportTemplate/ReportTemplateContext";
import { buildCapexYhteenvetoImage } from "../report/blocksPdf/renderCapexYhteenveto";

// Live-reading component, same family as Taloustaulu.jsx/PTSLongTermTable.js —
// reads usePTS()/useRakennusData()/ReportTemplateContext directly rather than
// taking { data, onChange }, since the numbers already exist in the PTS
// system. Registered as a plain report block (blockUtils.js) with no
// editable data of its own, and embedded directly on the Taloyhtiökortti
// PTS tab — both already provide these contexts.

// Same 4 PTS categories PiechartPTS.jsx uses, in the same order, with a new
// green-family ramp (CVD-separated, validated via the dataviz skill) instead
// of reusing PiechartPTS's own colors, which fail that validation. Every bar
// also carries a direct name+amount+% label, which is the skill's required
// mitigation for a ramp that clears CVD separation but not the stricter
// normal-vision floor.
const CATEGORIES = [
  { key: "tekniikkaYhteensa", name: "Rakennetekniikka", color: "#14532d" },
  { key: "lviYhteensa", name: "LVI Järjestelmät", color: "#2f8f4e" },
  { key: "sahkoYhteensa", name: "Sähköjärjestelmät", color: "#7cbf7f" },
  { key: "tutkimusYhteensa", name: "Lisätutkimukset", color: "#bfe0a8" },
];

function sumArea(buildings, field) {
  const total = buildings
    .filter(r => r.isMainBuilding)
    .reduce((sum, r) => {
      const val = Number(String(r[field] || "").replace(/\s/g, "").replace(",", "."));
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);
  return total || 1; // same divide-by-zero fallback as Taloustaulu.jsx
}

const sumThousands = (arr = []) => arr.reduce((a, b) => a + (parseFloat(b) || 0), 0) * 1000;
const fmtEur = v => `${Math.round(v).toLocaleString("fi-FI")} €`;
const fmtM2Month = v => `${v.toFixed(2).replace(".", ",")} €/m²/kk`;

function StatTile({ label, value, sub }) {
  return (
    <div className="col-sm-4">
      <div className="border rounded p-3 h-100" style={{ background: "#fafafa" }}>
        <div className="text-muted" style={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
        <div className="fw-bold" style={{ fontSize: "1.3rem", color: "#198754" }}>{value}</div>
        {sub && <div className="text-muted" style={{ fontSize: "0.72rem" }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function CapexYhteenveto({ data } = {}) {
  const { years: allYears, currentYear, footerTotals: allFooterTotals, tekniikkaYhteensa, lviYhteensa, sahkoYhteensa, tutkimusYhteensa } = usePTS() || {};
  const { rakennusData } = useRakennusData();
  const reportContext = useContext(ReportTemplateContext);
  const mergedRakennusData = reportContext?.mergedRakennusData;

  const dataToUse = mergedRakennusData?.rakennukset_fulls?.length > 0 ? mergedRakennusData : rakennusData || {};
  const buildings = Array.isArray(dataToUse.rakennukset_fulls) ? dataToUse.rakennukset_fulls : [];

  if (!allFooterTotals?.length) return null; // PTS data not loaded yet

  // Display-only truncation (report-block setting) — the underlying PTS
  // data stays 11 years; only what's summed/drawn here is sliced.
  const hide11thYear = !!data?.hide11thYear;
  const years = hide11thYear ? allYears.slice(0, 10) : allYears;
  // Label-only — every total below is the same figure as entered in the PTS
  // tables (VAT-inclusive, no VAT math applied); this toggle only picks
  // which phrasing is printed next to them.
  const alviton = !!data?.alviton;
  const alvLabel = alviton ? "ALV 0 %" : "sis. ALV 25,5%";
  const footerTotals = allFooterTotals.slice(0, years.length);
  const sliceVals = (arr) => (arr || []).slice(0, years.length);

  const grossArea = sumArea(buildings, "kokonaisala");
  const netArea = sumArea(buildings, "huoneistoala");

  const capexTotal = sumThousands(footerTotals);
  const avgAnnual = capexTotal / years.length;
  const perGrossM2Month = avgAnnual / grossArea / 12;
  const perNetM2Month = avgAnnual / netArea / 12;

  // 0–1 / 2–5 / 6–10 years, bucketed off the existing 11-entry y1..y11 grid —
  // per the user's own wording, not the reference screenshot's 4th "prior to
  // closing" bucket, which nothing in this app can compute. The "6–10 v"
  // label stays fixed even with hide11thYear on — footerTotals is already
  // truncated to `years.length` above, and .slice() clamps to the array's
  // actual length on its own, so bucketSum(6, 10) safely sums whatever's
  // left (6–9) without needing a second, label-driven truncation.
  const bucketSum = (start, end) => sumThousands(footerTotals.slice(start, end + 1));
  const periods = [
    { label: "0–1 v", total: bucketSum(0, 0) },
    { label: "2–5 v", total: bucketSum(1, 5) },
    { label: "6–10 v", total: bucketSum(6, 10) },
  ];

  const categorySource = { tekniikkaYhteensa: sliceVals(tekniikkaYhteensa), lviYhteensa: sliceVals(lviYhteensa), sahkoYhteensa: sliceVals(sahkoYhteensa), tutkimusYhteensa: sliceVals(tutkimusYhteensa) };
  const categories = CATEGORIES.map(c => ({ ...c, total: sumThousands(categorySource[c.key]) }));
  const categoryGrandTotal = categories.reduce((a, c) => a + c.total, 0) || 1;

  // Per-year, matching the reference's own "cost estimate per year" chart
  // (one bar per year) — the 0–1/2–5/6–10 bucketing is for the
  // toimenpide-ehdotukset summary box only, not this chart.
  const yearChartData = years.map((y, idx) => ({
    year: y,
    total: Math.round(footerTotals[idx] || 0),
  }));

  // Lets the exact image that would be embedded in the PDF be checked
  // directly — without generating the whole PDF — by building it with the
  // same function the PDF renderer uses and opening it in a new tab.
  const handlePreviewPdfImage = () => {
    const imageBase64 = buildCapexYhteenvetoImage({
      ptsContext: { years: allYears, currentYear, footerTotals: allFooterTotals, tekniikkaYhteensa, lviYhteensa, sahkoYhteensa, tutkimusYhteensa },
      rakennusData,
      mergedRakennusData,
      hide11thYear,
      alviton,
    });
    if (!imageBase64) return;
    const byteString = atob(imageBase64.split(",")[1]);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const blobUrl = URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
    window.open(blobUrl, "_blank");
  };

  return (
    <div className="mb-4 mx-auto w-100" style={{ maxWidth: "960px" }}>
      <style>{`
        .capex-section-title { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #198754; margin-bottom: 10px; }
      `}</style>

      <div className="border rounded p-3 mb-3" style={{ background: "#fff" }}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="capex-section-title">Capex-yhteenveto</div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: "0.7rem" }}
            onClick={handlePreviewPdfImage}
          >
            Esikatsele PDF-kuva
          </button>
        </div>

        <div className="row g-3 mb-3">
          <StatTile label="Capex yhteensä (10 vuotta)" value={fmtEur(capexTotal)}
            sub={`${alvLabel}, ${currentYear} hintataso`} />
          <StatTile label="Bruttoalaa kohden" value={fmtM2Month(perGrossM2Month)}
            sub={`${grossArea.toLocaleString("fi-FI")} m² kokonaisala`} />
          <StatTile label="Nettohuonealaa kohden" value={fmtM2Month(perNetM2Month)}
            sub={`${netArea.toLocaleString("fi-FI")} m² huoneistoala`} />
        </div>

        {/* Two columns side by side, matching the reference layout: the
            technical-area breakdown on the left, the per-year table+chart
            on the right — rather than everything stacked full-width. */}
        <div className="row g-4 mb-3">
          <div className="col-12 col-md-5">
            <div className="capex-section-title">Jakautuminen</div>
            {categories.map(c => {
              const pct = (c.total / categoryGrandTotal) * 100;
              return (
                <div key={c.name} className="mb-2">
                  <div className="d-flex justify-content-between" style={{ fontSize: "0.78rem" }}>
                    <span className="fw-semibold">{c.name}</span>
                    <span>{fmtEur(c.total)} · {pct.toFixed(0)} %</span>
                  </div>
                  <div style={{ height: 10, borderRadius: 5, background: "#eee", overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: c.color, borderRadius: 5 }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="col-12 col-md-7">
            <div className="capex-section-title">Kustannusarvio vuosittain, 1000 €</div>
            <div className="table-responsive mb-2">
              <table className="table table-sm mb-0" style={{ fontSize: "0.68rem" }}>
                <thead>
                  <tr>
                    <th className="text-muted fw-normal"></th>
                    {years.map(y => <th key={y} className="text-center fw-normal text-muted">{String(y).slice(-2)}</th>)}
                    <th className="text-center fw-bold">Yht.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td></td>
                    {footerTotals.map((v, idx) => (
                      <td key={idx} className="text-center">
                        {Math.round(v || 0)}
                      </td>
                    ))}
                    <td className="text-center fw-bold">{Math.round(capexTotal / 1000)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ width: "100%", height: 180 }}>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={yearChartData} margin={{ top: 16, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="year" tick={{ fontSize: 10, fontFamily: "Lato" }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => fmtEur(v)} />
                  <Bar dataKey="total" fill="#198754" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="capex-section-title">Toimenpide-ehdotusten yhteenveto <span className="text-muted" style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none" }}>({alvLabel})</span></div>
        <div className="border rounded p-3" style={{ background: "#eafbf1" }}>
          <div className="d-flex text-center">
            {periods.map((p, idx) => (
              <div key={p.label} className="flex-fill px-2" style={idx > 0 ? { borderLeft: "1px solid #bfe0a8" } : undefined}>
                <div className="text-muted" style={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>{p.label}</div>
                <div className="fw-bold" style={{ fontSize: "1.05rem", color: "#198754" }}>{fmtEur(p.total)}</div>
              </div>
            ))}
            <div className="flex-fill px-2" style={{ borderLeft: "1px solid #bfe0a8" }}>
              <div className="text-muted" style={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>Yhteensä</div>
              <div className="fw-bold" style={{ fontSize: "1.05rem", color: "#198754" }}>{fmtEur(capexTotal)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
